import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';

interface PaymentGatewayPageProps {
  customerData?: {
    fullName: string;
    mobileNumber: string;
  };
  amountData?: {
    amount: number;
    quantity: number;
    donationType: string;
    ratePerGram: number;
  };
  onPaymentComplete?: (paymentData: any) => void;
}

const PaymentGatewayPage: React.FC<PaymentGatewayPageProps> = ({ 
  customerData: propCustomerData, 
  amountData: propAmountData,
  onPaymentComplete 
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get data from navigation state or props
  const customerData = location.state?.customerData || propCustomerData;
  const amountData = location.state?.amountData || propAmountData;
  const [selectedGateway, setSelectedGateway] = useState('sahkar_pg'); // Default to Sahkar PG
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const paymentGateways = [
    {
      id: 'sahkar_pg',
      name: 'Sahkar Payment Gateway',
      description: 'UPI QR, Payment Links, Webhooks, Refunds',
      icon: '🏦',
      color: 'bg-blue-500',
      isPrimary: true
    }
  ];

  const handleGatewaySelect = (gatewayId: string) => {
    setSelectedGateway(gatewayId);
    setError('');
  };

  const handleProceedPayment = async () => {
    if (!selectedGateway) {
      setError(t('pages.paymentGateway.errors.selectPaymentMethod') as string);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const paymentData = {
        gateway: selectedGateway,
        customerData,
        amountData,
        timestamp: new Date().toISOString()
      };

      if (onPaymentComplete) {
        onPaymentComplete(paymentData);
      } else {
        // Navigate to payment processing or success page
        navigate('/payment-processing', { 
          state: paymentData 
        });
      }
    } catch (error) {
      setError(t('pages.paymentGateway.errors.paymentInitFailed') as string);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToAmount = () => {
    navigate('/enter-amount');
  };

  return (
    <div className="min-h-screen bg-black/20 backdrop-blur-sm flex items-center justify-center" style={{backgroundImage: 'url(/images/Asset5.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed'}}>
      <div className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto px-4 py-4 sm:py-6 md:py-8">
        {/* Language Switcher */}
        <div className="absolute top-4 right-4 z-10">
          <LanguageSwitcher />
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-400 to-orange-500 p-4 sm:p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full border-2 border-white flex items-center justify-center">
                <img 
                  src="/images/Asset2.png" 
                  alt="Temple Logo" 
                  className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                />
              </div>
            </div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white">{t('pages.paymentGateway.title')}</h1>
            <p className="text-white/90 text-xs sm:text-sm mt-1">{t('pages.paymentGateway.subtitle')}</p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Page Title */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{t('pages.paymentGateway.choosePaymentMethod')}</h2>
            </div>

            {/* Donation Summary */}
            {customerData && amountData && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center mb-4">
                  <span className="text-blue-600 text-lg mr-2">📋</span>
                  <h3 className="text-lg font-semibold text-blue-800">{t('pages.paymentGateway.donationSummary')}</h3>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-2">
                    <div>
                      <span className="text-gray-600">{t('pages.paymentGateway.devoteeName')}</span>
                      <div className="font-semibold text-gray-800">{customerData.fullName}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">{t('pages.paymentGateway.amount')}</span>
                      <div className="font-semibold text-gray-800">₹{amountData.amount.toFixed(2)}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">{t('pages.paymentGateway.quantity')}</span>
                      <div className="font-semibold text-gray-800">{amountData.quantity} g</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <span className="text-gray-600">{t('pages.paymentGateway.phoneNumber')}</span>
                      <div className="font-semibold text-gray-800">{customerData.mobileNumber}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">{t('pages.paymentGateway.donationType')}</span>
                      <div className="font-semibold text-gray-800">{amountData.donationType}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">{t('pages.paymentGateway.ratePerGram')}</span>
                      <div className="font-semibold text-gray-800">₹{amountData.ratePerGram.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Gateway Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('pages.paymentGateway.paymentGateway')}</h3>
              <div className="space-y-3">
                {paymentGateways.map((gateway) => (
                  <div
                    key={gateway.id}
                    onClick={() => handleGatewaySelect(gateway.id)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedGateway === gateway.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 ${gateway.color} rounded-lg flex items-center justify-center text-white text-xl`}>
                        {gateway.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{gateway.name}</h4>
                        <p className="text-sm text-gray-600">{gateway.description}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {gateway.isPrimary && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                            {t('pages.paymentGateway.primary')}
                          </span>
                        )}
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          selectedGateway === gateway.id
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}>
                          {selectedGateway === gateway.id && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleProceedPayment}
                disabled={loading || !selectedGateway}
                className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                ) : null}
                {t('pages.paymentGateway.proceedWithPayment')}
              </button>
              
              <button
                onClick={handleBackToAmount}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                {t('pages.paymentGateway.backToOtp')}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {/* AUGMONT Logo */}
            <div className="bg-white rounded-lg px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">Powered By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center mx-auto">
                  <img 
                    src="/images/Asset1.png" 
                    alt="AUGMONT GOLD" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
            
            {/* Mandirsetu Initiative */}
            <div className="bg-white rounded-lg px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">An Initiative By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center mx-auto">
                  <img 
                    src="/images/mandirSetu.png" 
                    alt="An Initiative By Mandirsetu" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentGatewayPage;
